import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import { fetchAuctionByAktenzeichen, fetchAttachments, getAttachmentSignedUrl, trackPageView, trackSectionFocus } from '@/services/api';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import type { Database } from '@/lib/database.types';
import type { StrategicSection } from '@shared/types';

type AuctionRow = Database['public']['Tables']['auctions']['Row'];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getCountdown(endAt: string): string {
  const diff = new Date(endAt).getTime() - Date.now();
  if (diff <= 0) return 'Beendet';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function getStatusBadge(status: string): { label: string; cls: string } {
  const map: Record<string, { label: string; cls: string }> = {
    ENTWURF: { label: 'ENTWURF', cls: 'status-offen' },
    LAUFEND: { label: 'LAUFEND', cls: 'status-laufend' },
    ZUSCHLAG_VORLAEUFIG: { label: 'ZUSCHLAG (vorläufig)', cls: 'status-warning' },
    ZUSCHLAG_FINAL: { label: 'ZUSCHLAG (final)', cls: 'status-success' },
    BEENDET_OHNE_ZUSCHLAG: { label: 'Beendet (ohne Zuschlag)', cls: 'status-danger' },
    BEENDET_PRUEFUNG: { label: 'In Prüfung', cls: 'status-warning' },
    LAUFEND_RESERVE: { label: 'Reserve-Bietung laufend', cls: 'status-warning' },
  };
  return map[status] || { label: status, cls: 'status-offen' };
}

export function AuctionLandingPage() {
  const { aktenzeichen } = useParams<{ aktenzeichen: string }>();
  const [auction, setAuction] = useState<AuctionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { setSectionVisible, setSectionHidden, flushSections, setPageViewId } = useAnalyticsStore();
  const [intersectionObserver, setIO] = useState<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!aktenzeichen) return;

    async function load() {
      const data = await fetchAuctionByAktenzeichen(aktenzeichen);
      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setAuction(data);
      setLoading(false);
    }

    load();
  }, [aktenzeichen]);

  // Page view tracking
  useEffect(() => {
    if (!auction) return;

    async function track() {
      const pvId = await trackPageView(auction.id, document.referrer);
      if (pvId) setPageViewId(pvId);
    }
    track();
  }, [auction?.id]);

  // Section tracking via IntersectionObserver
  useEffect(() => {
    if (!auction) return;

    const sections = document.querySelectorAll('section[data-section-id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (!sectionId) return;
          if (entry.isIntersecting) {
            setSectionVisible(sectionId);
          } else {
            setSectionHidden(sectionId);
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach((s) => observer.observe(s));
    setIO(observer);

    return () => observer.disconnect();
  }, [auction?.id]);

  // Flush on unload
  useEffect(() => {
    function handleBeforeUnload() {
      const sections = flushSections(auction?.id || '');
      if (sections.length > 0) {
        const pvId = useAnalyticsStore.getState().getPageViewId();
        if (pvId) {
          navigator.sendBeacon(
            '/.netlify/functions/track-view',
            JSON.stringify({ action: 'section_focus_batch', page_view_id: pvId, sections })
          );
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [auction?.id]);

  if (loading) return <div className="max-w-content mx-auto py-12"><p className="text-text-secondary">Laden...</p></div>;
  if (notFound) {
    // Return 404-style content — per spec, must NOT reveal auction existence
    return <div className="max-w-content mx-auto py-12"><h1 className="text-2xl font-serif mb-4">Seite nicht gefunden</h1></div>;
  }
  if (!auction) return null;

  const statusInfo = getStatusBadge(auction.status);
  const strategicSections = (auction.strategische_einordnung as unknown as StrategicSection[]) || [];

  return (
    <div className="max-w-content mx-auto py-8">
      {/* Breadcrumb */}
      <nav className="text-sm text-text-muted mb-6">
        <Link to="/dashboard">Dashboard</Link>
        <span className="mx-2">/</span>
        <span className="text-text-secondary">{auction.titel}</span>
      </nav>

      {/* =========================================== */}
      {/* BLOCK A — Aktenkopf */}
      {/* =========================================== */}
      <section data-section-id="block-a-aktenkopf" className="mb-8 border-b border-surface-border pb-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-lg text-text-secondary">{auction.aktenzeichen}</span>
          <span className={`status-badge ${statusInfo.cls}`}>{statusInfo.label}</span>
        </div>
        <h1 className="text-2xl font-serif mb-3">{auction.titel}</h1>
        <p className="text-sm text-text-muted">
          Fristende: {formatDate(auction.end_at)}
          {auction.status === 'LAUFEND' && <span className="ml-2 font-mono">({getCountdown(auction.end_at)})</span>}
        </p>
      </section>

      {/* =========================================== */}
      {/* BLOCK B — Patentdaten */}
      {/* =========================================== */}
      <section data-section-id="block-b-patentdaten" className="mb-8 border-b border-surface-border pb-6">
        <h2 className="text-lg font-serif mb-4">Patentdaten</h2>
        <table>
          <tbody>
            <tr><th className="w-56">Patentnummer</th><td>{auction.patent_nummer}</td></tr>
            <tr><th>Anmeldetag</th><td>{formatDate(auction.anmeldetag)}</td></tr>
            <tr><th>Erteilungstag</th><td>{formatDate(auction.erteilungstag)}</td></tr>
            <tr><th>Ablaufdatum</th><td>{formatDate(auction.ablaufdatum)}</td></tr>
            <tr><th>Restlaufzeit</th><td>—</td></tr>
            <tr><th>Schutzländer</th><td>{auction.schutzlaender.join(', ')}</td></tr>
            <tr><th>IPC-Klassifikation</th><td className="font-mono">{auction.ipc_klasse}</td></tr>
            <tr><th>Aktueller Inhaber</th><td>{auction.aktueller_inhaber}</td></tr>
            <tr><th>Stand Jahresgebühren</th><td>{auction.jahresgebuehren_bis}</td></tr>
          </tbody>
        </table>
      </section>

      {/* =========================================== */}
      {/* BLOCK C — Technische Zusammenfassung */}
      {/* =========================================== */}
      <section data-section-id="block-c-technisch" className="mb-8 border-b border-surface-border pb-6">
        <h2 className="text-lg font-serif mb-4">Technische Zusammenfassung</h2>
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: marked.parse(auction.technische_zusammenfassung) as string }}
        />
      </section>

      {/* =========================================== */}
      {/* BLOCK D — Strategische Einordnung */}
      {/* =========================================== */}
      {strategicSections.length > 0 && (
        <section data-section-id="block-d-strategisch" className="mb-8 border-b border-surface-border pb-6">
          <h2 className="text-lg font-serif mb-4">Strategische Einordnung</h2>
          {strategicSections.map((sec, i) => (
            <section data-section-id={`strat-${sec.section_id}`} key={sec.section_id} className="mb-6">
              <h3 className="text-base font-serif font-medium mb-2">{i + 1}. {sec.titel}</h3>
              <div
                dangerouslySetInnerHTML={{ __html: marked.parse(sec.body) as string }}
                className="text-text-secondary prose prose-sm max-w-none"
              />
            </section>
          ))}
        </section>
      )}

      {/* =========================================== */}
      {/* BLOCK E — Verfahrenshinweis */}
      {/* =========================================== */}
      <section data-section-id="block-e-verfahren" className="mb-8 border-b border-surface-border pb-6">
        <div className="p-4 bg-surface-alt border border-surface-border rounded-sm">
          <p className="text-sm text-text-secondary">
            Für diese Auktion gelten die <a href="/agb" className="">Auktionsbedingungen</a>.
            {' '}Mindestgebot: {formatCurrency(auction.mindestgebot)}.
            {' '}Mindestschrittweite: {formatCurrency(auction.mindest_schrittweite)}.
          </p>
        </div>
      </section>

      {/* =========================================== */}
      {/* BLOCK F — Gebotsabgabe */}
      {/* =========================================== */}
      <section data-section-id="block-f-gebot" className="mb-8 border-b border-surface-border pb-6">
        <h2 className="text-lg font-serif mb-4">Gebotsabgabe</h2>
        <p className="text-sm text-text-muted">
          Gebotsabgabe erfolgt auf der{' '}
          <Link to={`/auktion/${auction.aktenzeichen}/bieten`} className="">Gebotsseite</Link>.
        </p>
      </section>

      {/* =========================================== */}
      {/* BLOCK G — Anhänge */}
      {/* =========================================== */}
      <section data-section-id="block-g-anhaenge" className="mb-8 border-b border-surface-border pb-6">
        <h2 className="text-lg font-serif mb-4">Anhänge</h2>
        <p className="text-sm text-text-muted">Download-Links werden nach Anmeldung freigeschaltet.</p>
      </section>

      {/* =========================================== */}
      {/* BLOCK H — Fragen zum Patent */}
      {/* =========================================== */}
      <section data-section-id="block-h-inquiry" className="mb-8">
        <h2 className="text-lg font-serif mb-3">Haben Sie Fragen zu diesem Patent?</h2>
        <p className="text-sm text-text-secondary mb-4">
          Ihre Anfrage wird direkt an den Patentinhaber weitergeleitet.
          Sie erhalten eine Antwort üblicherweise innerhalb weniger Stunden.
        </p>
        <InquiryForm auctionId={auction.id} />
      </section>
    </div>
  );
}

function InquiryForm({ auctionId }: { auctionId: string }) {
  const [frage, setFrage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (frage.length < 20) { setError('Frage muss mindestens 20 Zeichen haben.'); return; }
    if (frage.length > 2000) { setError('Frage darf maximal 2000 Zeichen haben.'); return; }

    const { submitInquiry } = await import('@/services/api');
    const result = await submitInquiry(auctionId, frage);

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error);
    }
  }

  if (submitted) {
    return (
      <div className="p-4 border border-status-success/20 bg-status-success/5 rounded-sm">
        <p className="text-sm text-status-success">
          Ihre Anfrage wurde übermittelt. Sie erhalten eine Antwort an die angegebene E-Mail-Adresse.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="inquiry-frage" className="block text-sm font-medium mb-1">Frage *</label>
        <textarea
          id="inquiry-frage"
          className="input-field"
          rows={5}
          value={frage}
          onChange={(e) => setFrage(e.target.value)}
          required
        />
        <p className="text-xs text-text-muted mt-1">{frage.length}/2000 Zeichen</p>
      </div>

      {error && <p className="text-sm text-status-danger">{error}</p>}

      <button type="submit" className="btn-primary">Frage absenden</button>
    </form>
  );
}
